/**
 * "Säkerhet jämfört med resultat" (pedagogy spec §3.3.5 item 4): the share of
 * right answers per confidence level against the diagonal of perfect
 * calibration. The problems are the main series; the flashcards are a second
 * one at levels 2–4, drawn once enough cards were rated, and never pooled with
 * the problems (a card is graded after its back is seen, §3.3.3), so the
 * caption reads the problem series only. Numbers come from `GET
 * /api/progress/overview`'s `calibration`; the caption rule is
 * `shared/evidence.ts#calibrationCaption`.
 */
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CALIBRATION_MIN_PER_LEVEL,
  CALIBRATION_MIN_RATED,
  calibrationCaption,
} from "@shared/evidence";
import { useTheme } from "@client/app/theme";
import { Card } from "@client/components/ui";
import { useLang, useT } from "@client/i18n";
import { formatNumber, formatPercent } from "@client/lib/format";
import type { CalibrationDto } from "./types";

interface ChartRow {
  label: string;
  /** Share right; null when the level has too few answers to show one. */
  share: number | null;
  /** The flashcard series: null at level 1 (a card has none) and when the level has too few. */
  cards: number | null;
  /** Perfect calibration: level 1 → 25 %, … level 4 → 100 %. */
  reference: number;
}

export function CalibrationCard({ calibration }: { calibration: CalibrationDto }) {
  const t = useT();
  const { lang } = useLang();
  const { theme } = useTheme();
  const dark = theme === "dark";
  const scope = (
    <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">{t("progress.calibration.scope")}</p>
  );

  if (calibration.rated < CALIBRATION_MIN_RATED) {
    return (
      <Card title={t("progress.calibration.title")} compact>
        <p className="text-sm text-ink-600 dark:text-ink-400">
          {t("progress.calibration.empty", { min: formatNumber(CALIBRATION_MIN_RATED, lang) })}
        </p>
        {scope}
      </Card>
    );
  }

  const shareOf = (n: number, rightCount: number): number | null =>
    n >= CALIBRATION_MIN_PER_LEVEL ? rightCount / n : null;
  // The card series needs its own minimum of rated cards, as the problems do (§3.3.5 item 4).
  const cardSeries =
    calibration.flashcards && calibration.flashcards.rated >= CALIBRATION_MIN_RATED
      ? calibration.flashcards
      : null;
  const cardLevel = (level: number) => cardSeries?.levels.find((l) => l.level === level);
  const rows: ChartRow[] = calibration.levels.map((l) => {
    const card = cardLevel(l.level);
    return {
      label: t(`common.confidence.${l.level}`),
      share: shareOf(l.n, l.rightCount),
      cards: card ? shareOf(card.n, card.rightCount) : null,
      reference: l.level / 4,
    };
  });
  const caption = calibrationCaption(calibration);
  const captionText = t(`progress.calibration.caption.${caption}`);

  const barColor = dark ? "#818cf8" : "#4f46e5"; // accent-400 / accent-600
  const cardColor = dark ? "#fbbf24" : "#d97706"; // amber-400 / amber-600
  const referenceColor = dark ? "#94a3b8" : "#64748b"; // ink-400 / ink-500
  const tick = { fontSize: 11, fill: dark ? "#94a3b8" : "#64748b" };

  // A 0 % bar renders at zero pixel height and disappears (review UX-09): give
  // it a visible minimum via `minPointSize` on the <Bar>, and a "0 %" value
  // label above the hairline so it reads as data, not empty space. A level
  // with too few answers also has zero pixel height (recharts maps `share:
  // null` to `undefined`, which the scale also renders at height 0), so a
  // constant `minPointSize` would draw the same hairline for it — with no "0
  // %" label, it would read as an (invisible) 0 % bar instead of "too few
  // answers". The function form is called with the raw value (`null` for a
  // too-few level), so only an actual zero share gets lifted.
  const zeroLabelText = formatPercent(0, lang);
  function renderZeroShareLabel(props: {
    x?: number;
    y?: number;
    width?: number;
    value?: number | string;
  }) {
    const { x, y, width, value } = props;
    if (value !== 0 || x === undefined || y === undefined || width === undefined) return <g />;
    return (
      <text x={x + width / 2} y={y - 4} textAnchor="middle" fontSize={10} fill={tick.fill}>
        {zeroLabelText}
      </text>
    );
  }

  return (
    <Card title={t("progress.calibration.title")} compact>
      <div role="img" aria-label={t("progress.calibration.chartLabel")} className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 16 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={dark ? "#1e293b" : "#e2e8f0"}
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={tick}
              axisLine={false}
              tickLine={false}
              label={{
                value: t("progress.calibration.axisX"),
                position: "insideBottom",
                offset: -12,
                fill: tick.fill,
                fontSize: 11,
              }}
            />
            <YAxis
              domain={[0, 1]}
              ticks={[0, 0.25, 0.5, 0.75, 1]}
              tickFormatter={(value: number) => formatPercent(value, lang)}
              tick={tick}
              width={44}
              axisLine={false}
              tickLine={false}
              label={{
                value: t("progress.calibration.axisY"),
                angle: -90,
                position: "insideLeft",
                fill: tick.fill,
                fontSize: 11,
              }}
            />
            <Tooltip
              formatter={(value) =>
                typeof value === "number"
                  ? formatPercent(value, lang)
                  : t("progress.calibration.tooFew")
              }
              cursor={{ fill: dark ? "#1e293b" : "#f1f5f9" }}
            />
            <Legend
              verticalAlign="top"
              height={24}
              wrapperStyle={{ fontSize: 11, color: tick.fill }}
            />
            <Bar
              dataKey="share"
              name={t("progress.calibration.series.problems")}
              fill={barColor}
              radius={[2, 2, 0, 0]}
              minPointSize={(value) => (value === 0 ? 2 : 0)}
              label={renderZeroShareLabel}
              // Matches the `<Line>` below and the teacher charts
              // (ChapterAccuracyChart.tsx): the animated bars start at height
              // 0, so a test reading the DOM right after render finds no bar
              // `<path>`s, and in the real app the default animation races
              // `ResponsiveContainer`'s own mobile sizing on mount.
              isAnimationActive={false}
            />
            {cardSeries && (
              <Bar
                dataKey="cards"
                name={t("progress.calibration.series.flashcards")}
                fill={cardColor}
                radius={[2, 2, 0, 0]}
                minPointSize={(value) => (value === 0 ? 2 : 0)}
                label={renderZeroShareLabel}
                isAnimationActive={false}
              />
            )}
            <Line
              dataKey="reference"
              name={t("progress.calibration.reference")}
              stroke={referenceColor}
              strokeDasharray="4 4"
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <table className="sr-only">
        <caption>{t("progress.calibration.title")}</caption>
        <thead>
          <tr>
            <th scope="col">{t("progress.calibration.table.level")}</th>
            <th scope="col">{t("progress.calibration.table.answers")}</th>
            <th scope="col">{t("progress.calibration.table.right")}</th>
            {cardSeries && (
              <>
                <th scope="col">{t("progress.calibration.table.cardAnswers")}</th>
                <th scope="col">{t("progress.calibration.table.cardRight")}</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {calibration.levels.map((l) => {
            const share = shareOf(l.n, l.rightCount);
            const card = cardLevel(l.level);
            const cardShare = card ? shareOf(card.n, card.rightCount) : null;
            return (
              <tr key={l.level}>
                <th scope="row">{t(`common.confidence.${l.level}`)}</th>
                <td>{formatNumber(l.n, lang)}</td>
                <td>
                  {share === null ? t("progress.calibration.tooFew") : formatPercent(share, lang)}
                </td>
                {cardSeries && (
                  <>
                    <td>{card ? formatNumber(card.n, lang) : "–"}</td>
                    <td>
                      {!card
                        ? "–"
                        : cardShare === null
                          ? t("progress.calibration.tooFew")
                          : formatPercent(cardShare, lang)}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="mt-3 text-sm text-ink-800 dark:text-ink-100">{captionText}</p>
      {scope}
      {cardSeries && (
        <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
          {t("progress.calibration.flashcardsScope")}
        </p>
      )}
    </Card>
  );
}

export default CalibrationCard;
